import { SiteNavbar } from "@/site-kit/components/SiteNavbar";
import { ThemeToggle } from "@/site-kit/components/ThemeToggle";
import { ArrowRight, Terminal, Layers, Blocks, Sparkles } from "lucide-react";

const logo = (
  <span className="font-semibold text-lg tracking-tight">
    <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
      AI Apps
    </span>
  </span>
);

const navLinks = [
  { href: "/docs", label: "Docs" },
  {
    href: "https://www.npmjs.com/package/aiapps",
    label: "npm",
    external: true as const,
  },
  { href: "https://github.com", label: "GitHub", external: true as const },
];

export function LandingPage() {
  return (
    <div className="min-h-screen bg-white dark:bg-zinc-950">
      <SiteNavbar
        logo={logo}
        navLinks={navLinks}
        variant="homepage"
        rightContent={<ThemeToggle />}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="mx-auto max-w-6xl px-6 pt-20 pb-24 md:pt-32 md:pb-36">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 rounded-full border border-zinc-200 dark:border-zinc-800 bg-zinc-100 dark:bg-zinc-900 px-3 py-1 text-xs font-medium text-zinc-600 dark:text-zinc-400 mb-6">
              <Sparkles className="h-3 w-3" />
              Now in beta
            </div>

            <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100 leading-[1.1] mb-6">
              The framework for{" "}
              <span className="bg-gradient-to-r from-blue-600 to-violet-600 bg-clip-text text-transparent">
                building AI apps
              </span>
            </h1>

            <p className="text-lg md:text-xl text-zinc-600 dark:text-zinc-400 leading-relaxed max-w-2xl mb-10">
              AI Apps gives you the building blocks to create production-ready
              AI applications. Define flows, manage state, and ship faster with
              a framework designed for the AI-native era.
            </p>

            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="/docs"
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 px-6 py-3 text-sm font-semibold hover:opacity-90 transition-opacity"
              >
                Get started
                <ArrowRight className="h-4 w-4" />
              </a>
              <a
                href="https://www.npmjs.com/package/aiapps"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center justify-center gap-2 rounded-lg border border-zinc-200 dark:border-zinc-800 px-6 py-3 text-sm font-semibold hover:bg-zinc-50 dark:hover:bg-zinc-900 transition-colors"
              >
                <Terminal className="h-4 w-4" />
                npm install aiapps
              </a>
            </div>
          </div>
        </div>

        <div className="absolute -top-40 -right-40 h-[500px] w-[500px] rounded-full bg-gradient-to-br from-blue-500/10 to-violet-500/10 blur-3xl pointer-events-none" />
      </section>

      {/* Features */}
      <section className="border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/30">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 dark:text-zinc-100 mb-4">
              Everything you need
            </h2>
            <p className="text-zinc-600 dark:text-zinc-400 text-lg max-w-xl mx-auto">
              A complete toolkit for building AI-powered applications from the
              ground up.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Layers,
                title: "Structured flows",
                description:
                  "Define multi-step AI workflows with branching logic, retries, and human-in-the-loop checkpoints.",
              },
              {
                icon: Blocks,
                title: "Composable primitives",
                description:
                  "Build with modular blocks -- prompts, tools, memory, and evaluation -- that snap together cleanly.",
              },
              {
                icon: Terminal,
                title: "TypeScript-first",
                description:
                  "Full type safety from end to end. Autocomplete your way through flow definitions, tool schemas, and more.",
              },
            ].map((feature, i) => (
              <div
                key={i}
                className="group rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 p-8 hover:shadow-md transition-shadow"
              >
                <div className="mb-5 inline-flex rounded-lg bg-zinc-100 dark:bg-zinc-900 p-3">
                  <feature.icon className="h-5 w-5 text-zinc-900 dark:text-zinc-100" />
                </div>
                <h3 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100 mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-zinc-600 dark:text-zinc-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Code example */}
      <section className="border-t border-zinc-200 dark:border-zinc-800">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-28">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 dark:text-zinc-100 mb-4">
                Up and running in minutes
              </h2>
              <p className="text-zinc-600 dark:text-zinc-400 text-lg leading-relaxed mb-6">
                Install the package, define your first flow, and start building.
                No boilerplate, no configuration ceremony.
              </p>
              <a
                href="/docs"
                className="inline-flex items-center gap-2 text-sm font-semibold text-blue-600 dark:text-blue-400 hover:underline"
              >
                Read the quickstart guide
                <ArrowRight className="h-4 w-4" />
              </a>
            </div>
            <div className="rounded-xl border border-zinc-200 dark:border-zinc-800 bg-white dark:bg-zinc-950 overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/30">
                <div className="h-3 w-3 rounded-full bg-red-400/50" />
                <div className="h-3 w-3 rounded-full bg-yellow-400/50" />
                <div className="h-3 w-3 rounded-full bg-green-400/50" />
                <span className="ml-2 text-xs text-zinc-500 dark:text-zinc-400">
                  index.ts
                </span>
              </div>
              <pre className="p-5 text-sm leading-relaxed overflow-x-auto">
                <code className="text-zinc-800 dark:text-zinc-200">
                  {`import { createApp } from "aiapps";

const app = createApp({
  name: "my-ai-app",
});

app.flow("onboard-user", async (ctx) => {
  const name = await ctx.ask("What's your name?");
  await ctx.tool("create-profile", { name });
  return ctx.complete();
});

app.start();`}
                </code>
              </pre>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="border-t border-zinc-200 dark:border-zinc-800 bg-zinc-50/50 dark:bg-zinc-900/30">
        <div className="mx-auto max-w-6xl px-6 py-20 md:py-28 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-zinc-900 dark:text-zinc-100 mb-4">
            Start building today
          </h2>
          <p className="text-zinc-600 dark:text-zinc-400 text-lg max-w-xl mx-auto mb-8">
            Read the docs, install the package, and ship your first AI app.
          </p>
          <a
            href="/docs"
            className="inline-flex items-center justify-center gap-2 rounded-lg bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 px-8 py-3 text-sm font-semibold hover:opacity-90 transition-opacity"
          >
            Get started
            <ArrowRight className="h-4 w-4" />
          </a>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-zinc-200 dark:border-zinc-800">
        <div className="mx-auto max-w-6xl px-6 py-8 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-sm text-zinc-500 dark:text-zinc-400">
            &copy; {new Date().getFullYear()} AI Apps. All rights reserved.
          </p>
          <div className="flex items-center gap-6">
            <a
              href="https://taias.xyz"
              className="text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
            >
              Taias
            </a>
            <a
              href="https://github.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-sm text-zinc-500 dark:text-zinc-400 hover:text-zinc-900 dark:hover:text-zinc-100 transition-colors"
            >
              GitHub
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
}
